import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { produce } from 'immer';
import type { Draft } from 'immer';
import React, { useEffect, useMemo, useReducer, useContext, createContext } from 'react';
import type { ReactNode } from 'react';

import type { RootState } from 'store';

import {
  createReportConfiguration,
  updateReportConfiguration,
} from 'app-components/flux/report-configurations/reportConfigurationsActions';
import { getReportConfigByUniqueIdentifierWeb } from 'app-components/flux/report-configurations/reportConfigurationsSelectors';
import type {
  DateRangeType,
  DisplayOptionsType,
  OptionsType,
  ReportNameType,
  ReportSubType,
  ReportType,
  RowType,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import type { Timestep } from 'app-components/utils/date';

import {
  transformReportConfigurationToState,
  transformStateToReportConfiguration,
} from 'containers/NewReports/transformers';
import type { ReportAction, ReportSubTypeWebReports, ReportsState } from 'containers/NewReports/types';
import {
  RESET_DISPLAY_OPTIONS,
  RESET_REPORT,
  UPDATE_ACCOUNTING_TYPE,
  UPDATE_BREADCRUMBS,
  UPDATE_CHART_TYPE,
  UPDATE_DATE_RANGE,
  UPDATE_DISPLAY_OPTIONS,
  UPDATE_FILTER,
  UPDATE_GROUP_BY,
  UPDATE_LOADING,
  UPDATE_OPTIONS,
  UPDATE_OVERTIME,
  UPDATE_REPORT_SUBTYPE,
  UPDATE_REPORT_TYPE,
  UPDATE_SEGMENT_BY,
  UPDATE_TAB_NAME,
  UPDATE_TIMESTEP,
  UPDATE_USAGE,
} from 'containers/NewReports/types';
import { useReportState } from 'containers/NewReports/useReportState';
import useStatesHelper from 'containers/NewReports/useStatesHelper';
import { isEqualState } from 'containers/NewReports/utils';

export const fixupReportTypeIfInvalid = (draft: Draft<ReportsState>, reportType: ReportType): void => {
  if (draft.reportSubType === 'TAX_SUMMARY' && draft.usage?.includes('PERSONAL')) {
    draft.reportType = 'TRANSACTION';
    draft.timestep = undefined;
  } else {
    draft.reportType = reportType;
  }
};

///////////////////////////////////////////////////////////////////////////////////////////////////
// Report SubType Defaults & Validation
///////////////////////////////////////////////////////////////////////////////////////////////////

export const getDefaultReportSubtype = (state: Draft<ReportsState>): ReportSubTypeWebReports => {
  switch (state.reportType) {
    case 'SUMMARY':
      return 'NET_INCOME';
    case 'TRANSACTION':
      return 'TAX_SCHEDULE';
    case 'COMPARISON':
      return 'NET_INCOME';
  }
};

const validReportSubtypesByType: Record<ReportType, Set<ReportSubTypeWebReports>> = {
  SUMMARY: new Set(['TAX_SUMMARY', 'INCOME', 'NET_INCOME', 'CUSTOM']),
  TRANSACTION: new Set(['TAX_SCHEDULE', 'TAX_SUMMARY']),
  COMPARISON: new Set(),
};

const fixupReportSubtypeIfInvalid = (draft: Draft<ReportsState>): void => {
  const isReportSubtypeValid = validReportSubtypesByType[draft.reportType as ReportType]?.has(draft.reportSubType);
  if (!isReportSubtypeValid) {
    draft.reportSubType = getDefaultReportSubtype(draft);
  }

  if (draft.reportSubType === 'SAVINGS') {
    draft.reportSubType = 'NONE';
  }
};

///////////////////////////////////////////////////////////////////////////////////////////////////
// Report Group Defaults & Legalities
///////////////////////////////////////////////////////////////////////////////////////////////////

const getDefaultGroupBy = (state: Draft<ReportsState>): RowType => {
  // TODO: Need to handle business tier vs non-business tier
  switch (state.reportSubType) {
    case 'BALANCE_SHEET':
      return 'BUSINESS';
    case 'BALANCES_OVER_TIME':
      return 'NONE';
    case 'CUSTOM':
      return 'CATEGORY';
    case 'INCOME':
      return 'CATEGORY';
    case 'NET_INCOME':
      return 'CATEGORY';
    case 'NET_WORTH':
      return 'ACCOUNT';
    case 'PERFORMANCE_OVER_TIME':
      return 'NONE';
    case 'REFUND_TRACKER':
      return 'NONE';
    case 'SAVINGS':
      return 'ACCOUNT';
    case 'SPENDING':
      return 'CATEGORY';
    case 'TAX_SCHEDULE':
      return 'USAGE';
    case 'TAX_SUMMARY':
      return 'USAGE';
    default:
      return 'NONE'; // Default case to handle unhandled subtypes
  }
};

const validGroupByBySubtype: Record<ReportSubTypeWebReports, Set<RowType>> = {
  BALANCE_SHEET: new Set(['BUSINESS']),
  BALANCES_OVER_TIME: new Set(),
  CUSTOM: new Set(),
  INCOME: new Set(['ACCOUNT', 'CATEGORY', 'PAYEE', 'TAG', 'NONE']),
  NET_INCOME: new Set(['ACCOUNT', 'CATEGORY', 'PAYEE', 'TAG']),
  NET_WORTH: new Set(),
  PERFORMANCE_OVER_TIME: new Set(),
  REFUND_TRACKER: new Set(),
  SAVINGS: new Set(['ACCOUNT']),
  SPENDING: new Set(['ACCOUNT', 'CATEGORY', 'PAYEE', 'TAG']),
  TAX_SCHEDULE: new Set(),
  TAX_SUMMARY: new Set(['BUSINESS', 'TAX_LINE_ITEM', 'USAGE']),
  NONE: new Set(),
};

const fixupGroupByIfInvalid = (draft: Draft<ReportsState>): void => {
  const isReportGroupByValid = validGroupByBySubtype[draft.reportSubType]?.has(draft.groupBy);
  if (!isReportGroupByValid) {
    draft.groupBy = getDefaultGroupBy(draft);
  }
  if (draft.reportSubType === 'TAX_SUMMARY') {
    // Tax reports come in 2 flavors: either Detail (reportType === 'TRANSACTION') or Schedule (reportType === 'SUMMARY')
    draft.groupBy = draft.reportType === 'TRANSACTION' ? 'USAGE' : 'BUSINESS';
    draft.segmentBy = draft.reportType === 'TRANSACTION' ? 'NONE' : 'TIME';
    draft.timestep = draft.reportType === 'TRANSACTION' ? undefined : 'YEAR';
  }
};
///////////////////////////////////////////////////////////////////////////////////////////////////
// Report Segment Defaults & Legalities
///////////////////////////////////////////////////////////////////////////////////////////////////

const fixupSegmentByIfInvalid = (draft: Draft<ReportsState>): void => {
  let isSegmentByValid = true;
  if (draft.reportType === 'TRANSACTION' && draft.segmentBy !== 'NONE') {
    isSegmentByValid = false;
  } else if (draft.groupBy === draft.segmentBy) {
    isSegmentByValid = false;
  }
  if (!isSegmentByValid) {
    draft.segmentBy = 'TIME';
  }
};

///////////////////////////////////////////////////////////////////////////////////////////////////
// Updaters
///////////////////////////////////////////////////////////////////////////////////////////////////

const updateReportType = (draft: Draft<ReportsState>, reportType: ReportType) => {
  fixupReportTypeIfInvalid(draft, reportType);
  fixupReportSubtypeIfInvalid(draft);
  fixupGroupByIfInvalid(draft);
  fixupSegmentByIfInvalid(draft);
};

function updateReportSubtype(draft: Draft<ReportsState>, reportSubtype: ReportSubType) {
  draft.reportSubType = reportSubtype;
  fixupGroupByIfInvalid(draft);
  fixupSegmentByIfInvalid(draft);
}

const updateUsage = (draft: Draft<ReportsState>, usage: string[]) => {
  if (draft.reportSubType === 'TAX_SUMMARY') {
    if (usage[0] === 'PERSONAL') {
      draft.reportType = 'TRANSACTION';
      draft.reportSubType = 'TAX_SUMMARY';
      draft.groupBy = 'USAGE';
      draft.segmentBy = 'NONE';
      if (draft.reportSubType === 'TAX_SUMMARY' && draft.reportType === 'TRANSACTION') {
        delete draft.timestep;
      }
    }
  }
  draft.usage = usage;
};

const updateDateRange = (draft: Draft<ReportsState>, dateRange: DateRangeType) => {
  draft.dateRange = dateRange;
  if (draft.dateRange.interval && !draft.dateRange.id) {
    draft.dateRange.id = 'CUSTOM';
  }

  // don't change timestep for reportType === 'SUMMARY' in Tax reports
  if (draft.reportSubType === 'TAX_SUMMARY') {
    if (draft.reportType === 'TRANSACTION') {
      delete draft.timestep;
    }
    return;
  }

  // when date-range changes, update default timestep if one is already set
  if (draft.timestep !== 'NONE') {
    let timestep: Timestep = 'MONTH';
    if (dateRange.interval.count('days') <= 33 && draft.tabName !== 'net-income' && draft.tabName !== 'profit-loss') {
      timestep = 'DAY';
    } else if (dateRange.interval.count('months') > 36) {
      timestep = 'YEAR';
    }
    draft.timestep = timestep;
  }
};

const updateOptions = (draft: Draft<ReportsState>, values: Partial<OptionsType>) => {
  if (values) {
    Object.keys(values).forEach((key: keyof OptionsType) => {
      if (draft.options) {
        if (values[key] !== undefined) {
          // @ts-expect-error FIXME TS7053
          draft.options[key] = values[key];
        }
      }
    });
  }
};

const updateDisplayOptions = (draft: Draft<ReportsState>, values: Partial<DisplayOptionsType>) => {
  values &&
    Object.keys(values).forEach((key: keyof DisplayOptionsType) => {
      if (draft.displayOptions) {
        if (values[key] !== undefined) {
          // @ts-expect-error FIXME TS7053
          draft.displayOptions[key] = values[key];
        }
      }
    });
};

const ReportsStateReducer = (state: ReportsState, action: ReportAction) => {
  const newState = produce(state, (draft) => {
    switch (action.type) {
      case UPDATE_BREADCRUMBS:
        Object.assign(draft, action.payload);
        break;
      case UPDATE_CHART_TYPE:
        draft.chartType = action.payload.chartType;
        break;
      case UPDATE_DATE_RANGE:
        updateDateRange(draft, action.payload.dateRange);
        break;
      case UPDATE_DISPLAY_OPTIONS:
        updateDisplayOptions(draft, action.payload.displayOptions);
        break;
      case RESET_DISPLAY_OPTIONS:
        draft.accountingType = action.payload.accountingType;
        updateDisplayOptions(draft, action.payload.displayOptions);
        return;
      case RESET_REPORT: {
        Object.assign(draft, action.payload.state);
        break;
      }
      case UPDATE_OPTIONS:
        updateOptions(draft, action.payload.options);
        break;
      case UPDATE_FILTER:
        draft.filter = action.payload.filter;
        break;
      case UPDATE_SEGMENT_BY:
        draft.segmentBy = action.payload.segmentBy;
        break;
      case UPDATE_TIMESTEP:
        draft.timestep = action.payload.timestep;
        break;
      case UPDATE_ACCOUNTING_TYPE:
        draft.accountingType = action.payload.accountingType;
        break;
      case UPDATE_USAGE:
        updateUsage(draft, action.payload.usage);
        break;
      case UPDATE_REPORT_SUBTYPE:
        updateReportSubtype(draft, action.payload.reportSubtype);
        break;
      case UPDATE_REPORT_TYPE:
        updateReportType(draft, action.payload.reportType);
        break;
      case UPDATE_GROUP_BY:
        draft.groupBy = action.payload.groupBy;
        break;
      case UPDATE_OVERTIME:
        draft.timestep = action.payload.overTime.toUpperCase() === 'OVER-TIME' ? 'MONTH' : 'NONE';
        draft.chartType = action.payload.overTime.toUpperCase() === 'OVER-TIME' ? 'BAR_STACKED' : 'PIE';
        break;
      case UPDATE_LOADING:
        draft.loading = action.payload.loading;
        break;
      case UPDATE_TAB_NAME:
        draft.tabName = action.payload.tabName;
        break;
      default:
        break;
    }
  });

  return newState;
};

type ContextType = {
  state: ReportsState;
  setStateValue: (key: string, value: any) => void;
  dispatch: any;
};

export const StateContext = createContext<ContextType | undefined>(undefined);
StateContext.displayName = 'Reports State Context';

type StateProviderProps = {
  children?: ReactNode;
  tabName: ReportNameType;
  resetContext: (key: number, tabName: string) => void;
};

/////////////////////////////////////////////////////////////////////////////////////
// StateProvider Component
/////////////////////////////////////////////////////////////////////////////////////

const nonReportTabs: ReportNameType[] = ['monthly-summary'];

export const StateProvider = ({ tabName, children, resetContext }: StateProviderProps) => {
  // NOTE: whenever we change tabs, we establish a new initial state (Provider gets reloaded - see index.tsx)
  // if the initialState has an id, this means we found an existing reportConfiguration,
  // else this is the new configuration (see defaults in utils.tsx) for the tabName and we need to save it.
  const initialState = useReportState({ tabName });

  const [clientState, dispatch] = useReducer(ReportsStateReducer, initialState);
  const setStateValue = useStatesHelper({ state: clientState, dispatch });
  const reduxDispatch = useAppDispatch();
  const reportConfigurationFromStore = useAppSelector((s: Pick<RootState, 'reportConfigurationsStore'>) =>
    getReportConfigByUniqueIdentifierWeb(s, { persistentType: 'TEMPORARY', getTabName: () => tabName }),
  );

  useEffect(() => {
    // we change tabs by updating the tabName in state, so if the state.tabName !== tabName,
    // then we are switching tabs and are going to reinitialize the context provider (parent of this component).
    if (clientState.tabName !== tabName) {
      resetContext?.(Date.now(), clientState.tabName);
    }
  }, [resetContext, clientState.tabName, tabName]);

  // If reportConfigurationFromStore is undefined, then this report configuration doesn't exist. This only occurs ones per tab per dataset.
  // this means we need to create the reportconfiguration
  useEffect(() => {
    if (!tabName || nonReportTabs.includes(tabName)) return;

    if (!reportConfigurationFromStore) {
      // This only ever happens once per tab/per dataset.  id is not yet set in the state in this case.
      const reportConfigFromState = transformStateToReportConfiguration(initialState);
      reduxDispatch(createReportConfiguration(reportConfigFromState));
      return;
    }
  }, [reduxDispatch, initialState, reportConfigurationFromStore, tabName]);

  useEffect(() => {
    if (!tabName || nonReportTabs.includes(tabName)) return;
    if (tabName !== clientState.tabName) return; // we are switching tabs

    if (reportConfigurationFromStore) {
      const stateFromStore = transformReportConfigurationToState(reportConfigurationFromStore);

      // set the id and filter id based on the previously created configuration
      // filterId needs to be at the root level and also within the filter itself
      const { id, filterId, clientId } = reportConfigurationFromStore;
      const filterWithId = clientState.filter.set('id', filterId);
      const clientStateWithIds = { ...clientState, id, filterId, filter: filterWithId, clientId };

      if (!isEqualState(stateFromStore, clientStateWithIds)) {
        const reportConfigFromClientState = transformStateToReportConfiguration(clientStateWithIds);
        reduxDispatch(updateReportConfiguration(reportConfigFromClientState));
        return;
      }
    }
  }, [reduxDispatch, clientState, reportConfigurationFromStore, tabName]);

  const ctx = useMemo(() => ({ state: clientState, setStateValue, dispatch }), [setStateValue, clientState, dispatch]);
  return <StateContext.Provider value={ctx}>{children}</StateContext.Provider>;
};

export function useStateContext() {
  const context = useContext(StateContext);
  if (!context) {
    throw 'must pass state into provider';
  }
  return context;
}
